import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { esignAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { CheckCircle, PenTool, ShieldCheck, User, Building, DollarSign } from 'lucide-react';

const SECTION_META = {
  personal: { label: "Applicant's Personal Information", icon: User },
  corporate: { label: 'Corporate Information', icon: Building },
  net_worth: { label: 'Personal Net Worth', icon: DollarSign },
};

export default function ESignPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({});
  const [typedName, setTypedName] = useState('');
  const [coApplicantName, setCoApplicantName] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await esignAPI.get(token);
        setData(res.data);
        if (res.data.status === 'already_signed') {
          setSigned(true);
        } else {
          const prefilled = res.data.prefilled_fields || {};
          const fields = res.data.template_fields || [];
          const init = {};
          fields.forEach(f => { init[f.id] = prefilled[f.id] || ''; });
          setFormData(init);
          if (prefilled.applicant_name) setTypedName(prefilled.applicant_name);
        }
      } catch { setData({ error: true }); }
      setLoading(false);
    })();
  }, [token]);

  const handleSign = async () => {
    if (!typedName.trim()) { toast.error('Please type your full name to sign'); return; }
    if (!agreed) { toast.error('Please agree to the authorization'); return; }
    const fields = data?.template_fields || [];
    for (const f of fields) {
      if (f.required && !formData[f.id]?.toString().trim()) {
        toast.error(`"${f.label}" is required`);
        return;
      }
    }
    setSigning(true);
    try {
      await esignAPI.sign(token, {
        typed_name: typedName.trim(),
        co_applicant_name: coApplicantName.trim(),
        form_data: formData,
      });
      setSigned(true);
      toast.success('Application signed successfully!');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to sign'); }
    setSigning(false);
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-gray-100"><div className="animate-spin w-8 h-8 border-2 border-[#E31937] border-t-transparent rounded-full" /></div>;
  if (data?.error) return <div className="min-h-screen flex items-center justify-center bg-gray-100"><p className="text-gray-500">Invalid or expired link.</p></div>;

  if (signed) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
        <Card className="max-w-md w-full border-green-200 shadow-xl">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Application Signed</h1>
            <p className="text-gray-500">Your credit application has been successfully submitted. You may close this window.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const fields = data?.template_fields || [];
  const sections = ['personal', 'corporate', 'net_worth'].map(s => ({
    key: s,
    ...(SECTION_META[s] || { label: s, icon: User }),
    fields: fields.filter(f => f.section === s),
  })).filter(s => s.fields.length > 0);

  const widthClass = (w) => {
    switch (w) {
      case 'full': return 'col-span-6';
      case 'third': return 'col-span-6 sm:col-span-2';
      case 'quarter': return 'col-span-6 sm:col-span-3 lg:col-span-2';
      case 'sixth': return 'col-span-3 sm:col-span-1';
      default: return 'col-span-6 sm:col-span-3';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4" data-testid="esign-page">
      <div className="max-w-4xl mx-auto">
        {/* CTS Header */}
        <div className="bg-[#E31937] text-white text-center py-5 rounded-t-xl shadow-lg">
          <h1 className="text-2xl font-bold tracking-wider">CANADIAN TRUCK SALES</h1>
          <p className="text-xs opacity-80 mt-1">5939 Shawson Dr Suite #100, Mississauga, ON L4W 3Y2 | (416) 417-2525</p>
        </div>

        <Card className="border-0 shadow-xl rounded-t-none rounded-b-xl">
          <CardContent className="p-6 sm:p-8">
            {/* Title */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-bold text-gray-900">{data?.template_title || 'Credit Application'}</h2>
              {data?.template_description && (
                <p className="text-sm text-gray-500 mt-1">{data.template_description}</p>
              )}
              {(data?.deal_title || data?.unit_title) && (
                <div className="inline-block bg-gray-50 rounded-lg px-4 py-2 mt-3 text-sm border border-gray-200">
                  {data?.deal_title && <span className="text-gray-600">Deal: <strong>{data.deal_title}</strong></span>}
                  {data?.unit_title && <span className="text-gray-600 ml-3">Unit: <strong>{data.unit_title}</strong></span>}
                </div>
              )}
            </div>

            {/* Form Sections */}
            {sections.map(section => {
              const SIcon = section.icon;
              // Net worth section gets special layout
              if (section.key === 'net_worth') {
                const assets = section.fields.filter(f => f.sub_section === 'assets');
                const liabilities = section.fields.filter(f => f.sub_section === 'liabilities');
                return (
                  <div key={section.key} className="mb-6">
                    <div className="bg-[#E31937] text-white px-4 py-2 rounded-t-lg flex items-center gap-2">
                      <SIcon className="w-4 h-4" />
                      <h3 className="text-sm font-bold uppercase tracking-wide">{section.label}</h3>
                    </div>
                    <div className="border border-t-0 border-gray-200 rounded-b-lg p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Assets */}
                        <div>
                          <p className="text-xs font-bold text-gray-700 uppercase border-b border-gray-200 pb-1 mb-3">Assets</p>
                          <div className="space-y-2">
                            {assets.map(f => (
                              <div key={f.id} className="flex items-center gap-2">
                                <label className="text-xs text-gray-600 w-40 flex-shrink-0">{f.label}</label>
                                <Input type="text" value={formData[f.id] || ''} onChange={e => setFormData(p => ({ ...p, [f.id]: e.target.value }))} className="h-8 text-sm bg-white border-gray-300 flex-1" placeholder="$" data-testid={`field-${f.id}`} />
                              </div>
                            ))}
                          </div>
                        </div>
                        {/* Liabilities */}
                        <div>
                          <p className="text-xs font-bold text-gray-700 uppercase border-b border-gray-200 pb-1 mb-3">Liabilities</p>
                          <div className="space-y-2">
                            {liabilities.map(f => (
                              <div key={f.id} className="flex items-center gap-2">
                                <label className="text-xs text-gray-600 w-40 flex-shrink-0">{f.label}</label>
                                <Input type="text" value={formData[f.id] || ''} onChange={e => setFormData(p => ({ ...p, [f.id]: e.target.value }))} className="h-8 text-sm bg-white border-gray-300 flex-1" placeholder="$" data-testid={`field-${f.id}`} />
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }

              return (
                <div key={section.key} className="mb-6">
                  <div className="bg-[#E31937] text-white px-4 py-2 rounded-t-lg flex items-center gap-2">
                    <SIcon className="w-4 h-4" />
                    <h3 className="text-sm font-bold uppercase tracking-wide">{section.label}</h3>
                  </div>
                  <div className="border border-t-0 border-gray-200 rounded-b-lg p-4">
                    <div className="grid grid-cols-6 gap-x-3 gap-y-3">
                      {section.fields.map(f => (
                        <div key={f.id} className={widthClass(f.width)}>
                          <label className="text-xs font-medium text-gray-600 block mb-0.5">
                            {f.label} {f.required && <span className="text-red-500">*</span>}
                          </label>
                          {f.type === 'select' ? (
                            <Select value={formData[f.id] || ''} onValueChange={v => setFormData(p => ({ ...p, [f.id]: v }))}>
                              <SelectTrigger className="bg-white border-gray-300 h-9 text-sm" data-testid={`field-${f.id}`}><SelectValue placeholder="Select..." /></SelectTrigger>
                              <SelectContent>{(f.options || []).map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : f.type === 'textarea' ? (
                            <Textarea value={formData[f.id] || ''} onChange={e => setFormData(p => ({ ...p, [f.id]: e.target.value }))} className="bg-white border-gray-300 text-sm" rows={2} data-testid={`field-${f.id}`} />
                          ) : (
                            <Input type={f.type} value={formData[f.id] || ''} onChange={e => setFormData(p => ({ ...p, [f.id]: e.target.value }))} className="bg-white border-gray-300 h-9 text-sm" data-testid={`field-${f.id}`} />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Authorization & Certification */}
            <div className="mb-6">
              <div className="bg-gray-800 text-white px-4 py-2 rounded-t-lg">
                <h3 className="text-sm font-bold uppercase tracking-wide">Authorization & Certification</h3>
              </div>
              <div className="border border-t-0 border-gray-200 rounded-b-lg p-4">
                <p className="text-xs text-gray-600 leading-relaxed mb-4">{data?.authorization_text}</p>

                <label className="flex items-start gap-3 text-xs text-gray-700 cursor-pointer p-3 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors mb-4" data-testid="esign-agree-label">
                  <input type="checkbox" checked={agreed} onChange={e => setAgreed(e.target.checked)} className="mt-0.5 accent-[#E31937] w-4 h-4 flex-shrink-0" data-testid="esign-agree-checkbox" />
                  <span>I certify that all information provided above is true, complete, and accurate. I authorize Canadian Truck Sales to obtain credit reports and share information with lending partners as described above.</span>
                </label>

                {/* Signature Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
                  {/* Applicant Signature */}
                  <div>
                    <label className="text-xs font-bold text-gray-700 uppercase block mb-2">Applicant Signature</label>
                    <Input
                      value={typedName}
                      onChange={e => setTypedName(e.target.value)}
                      placeholder="Type your full legal name..."
                      className="h-12 border-gray-300 bg-white text-lg"
                      style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive", fontSize: '1.3rem' }}
                      data-testid="esign-name-input"
                    />
                    {typedName && (
                      <div className="mt-2 p-3 border-b-2 border-gray-900 bg-white text-center">
                        <p className="text-2xl text-gray-800" style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}>{typedName}</p>
                      </div>
                    )}
                    <p className="text-[10px] text-gray-400 mt-1">Date: {new Date().toLocaleDateString()}</p>
                  </div>
                  {/* Co-Applicant Signature */}
                  <div>
                    <label className="text-xs font-bold text-gray-700 uppercase block mb-2">Co-Applicant Signature (if applicable)</label>
                    <Input
                      value={coApplicantName}
                      onChange={e => setCoApplicantName(e.target.value)}
                      placeholder="Type co-applicant's full name..."
                      className="h-12 border-gray-300 bg-white text-lg"
                      style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive", fontSize: '1.3rem' }}
                      data-testid="esign-co-applicant-input"
                    />
                    {coApplicantName && (
                      <div className="mt-2 p-3 border-b-2 border-gray-900 bg-white text-center">
                        <p className="text-2xl text-gray-800" style={{ fontFamily: "'Brush Script MT', 'Segoe Script', cursive" }}>{coApplicantName}</p>
                      </div>
                    )}
                    <p className="text-[10px] text-gray-400 mt-1">Date: {new Date().toLocaleDateString()}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Submit */}
            <Button
              className="w-full bg-[#E31937] hover:bg-[#C4122C] text-white h-12 text-base font-bold shadow-lg"
              onClick={handleSign}
              disabled={signing || !typedName.trim() || !agreed}
              data-testid="esign-submit-btn"
            >
              {signing ? 'Submitting...' : 'Sign & Submit Credit Application'}
            </Button>

            <div className="flex items-center gap-2 text-[10px] text-gray-400 justify-center mt-3">
              <ShieldCheck className="w-3 h-3" />
              <span>Your IP address and timestamp will be recorded for verification purposes</span>
            </div>
          </CardContent>
        </Card>

        <p className="text-center text-[10px] text-gray-400 mt-4">
          Canadian Truck Sales | 5939 Shawson Dr Suite #100, Mississauga, ON L4W 3Y2 | (416) 417-2525
        </p>
      </div>
    </div>
  );
}
